/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { ComponentEnhancer, StateHandler, StateHandlerMap } from 'recompose';
import { branch, compose, getContext, mapProps, withHandlers, withProps, withStateHandlers } from 'recompose';

import Retracked from 'js/app/retracked';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import getModernContext from 'js/lib/getModernContext';
import { isPoweruser } from 'js/lib/user';

import { Grid, Pagination, Typography2 } from '@coursera/cds-core';

import { navigateToProgramCreation } from 'bundles/enterprise-admin-contracts/utils/navigate';
import AdminLandingAddFirstProgram from 'bundles/enterprise-admin-home/components/AdminLandingAddFirstProgram';
import ProgramListHeader from 'bundles/enterprise-admin-home/components/ProgramListHeader';
import ProgramSearchStatus from 'bundles/enterprise-admin-home/components/ProgramSearchStatus';
import ProgramSummaryList from 'bundles/enterprise-admin-home/components/ProgramSummaryList';
import type { PaginatedEnterprisePrograms } from 'bundles/enterprise-admin-home/types';
import { getProgramNameList, paginatePrograms } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import type { ProgramOrder, ProgramSort } from 'bundles/enterprise-admin-home/utils/program-sort';
import { getContractsLink } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import { shouldDisplayContract } from 'bundles/enterprise-admin-program-creation/utils/contract';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { Spacer } from 'bundles/enterprise-ui/components';
import type { OrganizationType } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';

import _t from 'i18n!nls/enterprise-admin-home';

const PROGRAMS_PER_PAGE = 5;

export type PropsFromCaller = {
  hasActiveProgram?: boolean;
  hasSelfServeContract: boolean;
  canManageOrganization: boolean;
  enterprisePrograms?: Array<EnterpriseProgramsV1>;
  thirdPartyOrganization: { id: string; slug: string; organizationType?: OrganizationType | null };
  programSort: ProgramSort;
  programOrder: ProgramOrder;
  contracts: Array<{ contractState?: string | null; isReal: boolean }>;
  onProgramSortChange: (sort: ProgramSort) => void;
  onProgramOrderChange: (order: ProgramOrder) => void;
};

type PropsFromRouter = {
  router: InjectedRouter;
  searchQuery: string;
  pageQuery: number;
  _eventData?: { [key: string]: string };
  thirdPartyOrganization?: { id: string };
};

type PropsFromGetContext = {
  enableAcademicDisciplines: boolean;
  enableNewProgramCreation: boolean;
  isCareerAcademySelfServeEnabled: boolean;
};

type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};

type PropsFromWithProps = {
  sortedPrograms: Array<EnterpriseProgramsV1>;
  programs: Array<EnterpriseProgramsV1>;
  showProgramList: boolean;
  programNameList: { id: string; name: string }[];
  showAddFirstProgram: boolean;
  showAddProgramButton: boolean;
  enableC4CBOnboarding?: boolean;
  enableGwGSetup?: boolean;
  enableGwGFreemiumSetup?: boolean;
  enableGwGPaidSetup?: boolean;
  enableC4CSetup?: boolean;
};

type PropsFromWithPropsDefiningIsPaginated = { isPaginated: boolean };
type PropsFromWithPropsDefiningPaginatedPrograms = { paginatedPrograms: PaginatedEnterprisePrograms };

type PropsFromWithStateHandlersForCurrentPage = { currentPage: number };

interface PropsFromWithStateHandlersUpdatersForCurrentPage
  extends StateHandlerMap<PropsFromWithStateHandlersForCurrentPage> {
  setCurrentPage: StateHandler<PropsFromWithStateHandlersForCurrentPage>;
}

type PropsFromMapProps = PropsFromWithPropsDefiningPaginatedPrograms &
  PropsFromWithPropsDefiningIsPaginated &
  PropsFromRouter;

type PropsFromWithHandlers = {
  handleUpdatePage: (event: React.ChangeEvent<unknown>, page: number) => void;
  onSearch: (query: string) => void;
  navigateToProgramContract: () => void;
};

type Props = PropsFromCaller &
  PropsFromRouter &
  PropsFromGetContext &
  PropsFromWithProps &
  PropsFromWithPropsDefiningIsPaginated &
  PropsFromWithPropsDefiningPaginatedPrograms &
  PropsFromWithStateHandlersForCurrentPage &
  PropsFromWithStateHandlersUpdatersForCurrentPage &
  PropsFromWithHandlers;

type State = {
  showProgramIds: boolean;
};

function searchPrograms(programs: Array<EnterpriseProgramsV1>, query: string): Array<EnterpriseProgramsV1> {
  return programs.filter((program) => program.name.toLowerCase().indexOf(query.toLowerCase()) > -1);
}

function updateRouter(router: InjectedRouter, pageQuery?: number, searchQuery?: string): void {
  const query = {
    page: pageQuery || undefined,
    search: searchQuery || undefined,
  };
  router.push({
    pathname: router.location.pathname,
    query,
  });
}

function isValidPage(pageQuery: number, isPaginated: boolean, paginatedPrograms: PaginatedEnterprisePrograms): boolean {
  const isLargerThanPagination = !!(pageQuery && isPaginated && pageQuery > Object.keys(paginatedPrograms).length);
  const isLargerThanSinglePage = !!(pageQuery && !isPaginated && pageQuery > 1);
  return !(isLargerThanPagination || isLargerThanSinglePage);
}

class ProgramSummaryListContainer extends React.Component<Props, State> {
  declare _listRef?: HTMLDivElement | null;

  state = {
    showProgramIds: false,
  } as State;

  hasContractsAvailable = () => {
    const { contracts } = this.props;
    return contracts.some(shouldDisplayContract);
  };

  componentDidMount() {
    // update router if illegal page url param
    const { router, pageQuery, isPaginated, paginatedPrograms, searchQuery } = this.props;
    const isValidPageQuery = isValidPage(pageQuery, isPaginated, paginatedPrograms);
    const validPage = isValidPageQuery ? pageQuery : 1;
    if (!isValidPageQuery) updateRouter(router, validPage, searchQuery);
  }

  componentDidUpdate(prevProps: Props): void {
    const { pageQuery, searchQuery, setCurrentPage, showAddFirstProgram, router } = this.props;
    if (prevProps.pageQuery !== pageQuery) {
      setCurrentPage(pageQuery || 1);
    }
    if (prevProps.searchQuery !== searchQuery) {
      this.setTypeAheadInput(searchQuery ?? '');
    }
    if (router.location.query.autoCreateProgram && showAddFirstProgram && this.hasContractsAvailable()) {
      this.handleAddProgram();
    }
  }

  handleShowingProgramIds = () => {
    const { showProgramIds } = this.state;
    this.setState({ showProgramIds: !showProgramIds });
  };

  // A HACK: sets typeahead inputValue
  setTypeAheadInput = (inputValue: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    const typeAhead = this.refs.TypeAheadV2 as { setState: Function };

    if (typeAhead) {
      typeAhead.setState(() => ({ inputValue }));
    }
  };

  clearSearch = (): void => {
    const { router } = this.props;
    updateRouter(router);
    this.setTypeAheadInput('');
  };

  // A HACK: sets typeahead placeholder
  overridePlaceholder = (typeAheadNode: HTMLDivElement | null): void => {
    if (typeAheadNode) {
      const input = typeAheadNode.querySelector('input');
      if (input) {
        input.placeholder = _t('Search programs...');
      }
    }
  };

  handleAddProgram = () => {
    const {
      navigateToProgramContract,
      sortedPrograms,
      enableC4CBOnboarding,
      enableNewProgramCreation,
      thirdPartyOrganization,
      router,
    } = this.props;
    const isFreemium = !!(enableC4CBOnboarding && sortedPrograms.length >= 1);
    if (this.hasContractsAvailable()) {
      return navigateToProgramCreation(isFreemium, enableNewProgramCreation, thirdPartyOrganization.slug, router);
    }

    return navigateToProgramContract();
  };

  render() {
    const {
      programs,
      searchQuery,
      currentPage,
      onSearch,
      isPaginated,
      sortedPrograms,
      programNameList,
      handleUpdatePage,
      paginatedPrograms,
      showProgramList,
      showAddFirstProgram,
      showAddProgramButton,
      canManageOrganization,
      thirdPartyOrganization,
      programSort,
      programOrder,
      onProgramSortChange,
      onProgramOrderChange,
      router,
    } = this.props;
    const displayedPrograms: Array<EnterpriseProgramsV1> = isPaginated ? paginatedPrograms[currentPage] : programs;
    const programsAreDisplayed = displayedPrograms && displayedPrograms.length > 0;
    const { showProgramIds } = this.state;
    if (router.location.query.autoCreateProgram) return null;
    const pagination = (
      <Grid container justifyContent="space-between" data-testid="program-pagination">
        <Typography2 variant="subtitleMedium" component="p">
          {_t('Showing #{count} of #{total}', {
            count: displayedPrograms && displayedPrograms.length,
            total: programs.length,
          })}
        </Typography2>
        <Pagination
          total={programs.length}
          page={currentPage}
          pageSize={PROGRAMS_PER_PAGE}
          onPageChange={handleUpdatePage}
          hidePageSizeInput
        />
      </Grid>
    );
    const disableAddProgramButton =
      !this.hasContractsAvailable() && !enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing() && !isPoweruser();

    return (
      <div id="program-list" data-e2e="Program_List" data-elementtiming="admin-dashboard.program-list">
        {showAddFirstProgram && (
          <AdminLandingAddFirstProgram
            canManageOrganization={canManageOrganization}
            navigateToProgramContract={this.handleAddProgram}
            disableAddProgramButton={disableAddProgramButton}
          />
        )}
        {showProgramList && (
          <ProgramListHeader
            withAbilityToShowProgramIds={enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing()}
            showProgramIds={showProgramIds}
            handleShowProgramIdsChange={this.handleShowingProgramIds}
            programNameList={programNameList}
            withAbilityToAddProgram={showAddProgramButton}
            disableAddProgramButton={disableAddProgramButton}
            thirdPartyOrganization={thirdPartyOrganization}
            onAddProgram={this.handleAddProgram}
            withAbilityToSearch={sortedPrograms && sortedPrograms.length > 1}
            searchProps={{
              onSearch,
              searchDocuments: programNameList,
              defaultValue: searchQuery || '',
              placeholder: _t('Search by Learning Program name'),
            }}
            programSort={programSort}
            programOrder={programOrder}
            onProgramSortChange={onProgramSortChange}
            onProgramOrderChange={onProgramOrderChange}
          />
        )}
        {showProgramList && <Spacer size={32} />}
        {showProgramList && searchQuery && (
          <ProgramSearchStatus searchQuery={searchQuery} clearSearch={this.clearSearch} numResults={programs.length} />
        )}
        {isPaginated && programsAreDisplayed && pagination}
        {isPaginated && programsAreDisplayed && <Spacer size={32} />}
        {showProgramList && programsAreDisplayed && (
          <ProgramSummaryList
            programs={displayedPrograms}
            showProgramIds={showProgramIds}
            thirdPartyOrganization={thirdPartyOrganization}
          />
        )}
        {isPaginated && programsAreDisplayed && <Spacer size={32} />}
        {isPaginated && programsAreDisplayed && pagination}
      </div>
    );
  }
}

const enhanced = compose<Props, PropsFromCaller>(
  getContext({
    _eventData: PropTypes.object,
    enableAcademicDisciplines: PropTypes.bool,
    enableNewProgramCreation: PropTypes.bool,
    isCareerAcademySelfServeEnabled: PropTypes.bool,
  }),
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext),
  connectToRouter<PropsFromCaller, PropsFromRouter>((router: InjectedRouter): PropsFromRouter => {
    const { page, search } = router.location.query;
    return {
      router,
      searchQuery: search,
      pageQuery: parseInt(page, 10),
    };
  }),
  withProps<
    PropsFromWithProps,
    PropsFromCaller & PropsFromGetContext & PropsFromEnterprisePermissionsContext & PropsFromRouter
  >((props) => {
    const {
      hasActiveProgram,
      hasSelfServeContract,
      canManageOrganization,
      enableAcademicDisciplines,
      enableNewProgramCreation,
      isCareerAcademySelfServeEnabled,
      enterpriseAdminPermissionsContext,
      enterprisePrograms = [],
      programSort,
      programOrder,
      searchQuery,
    } = props;

    const { hasPermission } = enterpriseAdminPermissionsContext;

    const visiblePrograms = enterprisePrograms.filter((program) =>
      hasPermission({
        domain: 'PROGRAM',
        domainId: program.id,
        verb: 'VIEW',
      })
    );
    const sortedPrograms = visiblePrograms.sort((a, b) => {
      const x = programOrder === 'ASC' ? 1 : -1;
      const condition = {
        CREATED: (a.launchedAt ?? 0) > (b.launchedAt ?? 0),
        NAME: a.metadata.name > b.metadata.name,
      }[programSort];
      return condition ? x : -x;
    });
    const programs = searchQuery ? searchPrograms(sortedPrograms, searchQuery) : sortedPrograms;
    const isSuperUserOrEnterpriseOutsourcing = enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing();
    const enableC4CBOnboarding = enterpriseAdminUser.isC4CBOrg;

    return {
      sortedPrograms,
      programs,
      showProgramList: hasActiveProgram === true,
      showAddFirstProgram:
        (hasSelfServeContract ||
          isSuperUserOrEnterpriseOutsourcing ||
          (!hasSelfServeContract && canManageOrganization && enableNewProgramCreation)) &&
        hasActiveProgram === false,
      showAddProgramButton:
        isSuperUserOrEnterpriseOutsourcing ||
        (canManageOrganization &&
          (hasSelfServeContract ||
            enableC4CBOnboarding ||
            enableAcademicDisciplines ||
            isCareerAcademySelfServeEnabled ||
            (!hasSelfServeContract && enableNewProgramCreation))),
      programNameList: getProgramNameList(sortedPrograms),
      enableC4CBOnboarding,
      enableNewProgramCreation,
    };
  }),
  withProps<PropsFromWithPropsDefiningIsPaginated, PropsFromWithProps>((props) => ({
    isPaginated: props.programs.length > PROGRAMS_PER_PAGE,
  })),
  branch<PropsFromWithPropsDefiningIsPaginated & PropsFromWithProps>(
    (props) => props.isPaginated,
    withProps<PropsFromWithPropsDefiningPaginatedPrograms, PropsFromWithPropsDefiningIsPaginated & PropsFromWithProps>(
      (props) => ({
        paginatedPrograms: paginatePrograms(props.programs, PROGRAMS_PER_PAGE),
      })
    ) as ComponentEnhancer<
      PropsFromWithPropsDefiningPaginatedPrograms,
      PropsFromWithPropsDefiningIsPaginated & PropsFromWithProps
    >
  ),
  // set page to 1 if illegal page url param
  mapProps<PropsFromMapProps, PropsFromMapProps>((props) => {
    const { pageQuery, isPaginated, paginatedPrograms } = props;
    const isValidPageQuery = isValidPage(pageQuery, isPaginated, paginatedPrograms);
    return {
      ...props,
      pageQuery: isValidPageQuery ? pageQuery : 1,
      isPaginated,
      paginatedPrograms,
    };
  }),
  withStateHandlers<
    PropsFromWithStateHandlersForCurrentPage,
    PropsFromWithStateHandlersUpdatersForCurrentPage,
    PropsFromRouter
  >(({ pageQuery }: { pageQuery: number }) => ({ currentPage: pageQuery || 1 }), {
    setCurrentPage:
      () =>
      (page: number): { currentPage: number } => ({ currentPage: page }),
  }),
  withHandlers<PropsFromRouter, PropsFromWithHandlers>({
    handleUpdatePage:
      ({ router, searchQuery, _eventData, thirdPartyOrganization }) =>
      (_, pageNumber) => {
        Retracked.trackComponent(
          _eventData,
          { thirdPartyOrganizationId: thirdPartyOrganization?.id },
          'program_list_pagination_control',
          'handleUpdatePage'
        );
        updateRouter(router, pageNumber, searchQuery);
      },
    onSearch:
      ({ router, _eventData, thirdPartyOrganization }) =>
      (programName) => {
        Retracked.trackComponent(
          _eventData,
          { thirdPartyOrganizationId: thirdPartyOrganization?.id },
          'program_list_search',
          'onSearch'
        );
        if (programName) {
          updateRouter(router, 1, programName);
        } else {
          updateRouter(router);
        }
      },
    navigateToProgramContract:
      ({ router }) =>
      () => {
        const { push, params } = router;
        push(getContractsLink(params));
      },
  })
);

export default enhanced(ProgramSummaryListContainer);
